import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  Container,
  useTheme,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import StrategiesTable from "./StrategiesTable";
import StrategiesDialog from "./StrategiesDialog";
import { Helmet } from "react-helmet-async";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
  removeActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { deleteStrategies, updateStrategy } from "../../utils/restAPI";
import { selectStrategies, selectManagers } from "../../Redux/Reducers/selectors";
import { setStrategies } from "../../Redux/Reducers/strategiesSlice";
import { getUserState } from "../../Redux/Reducers/userReducer";
import { toast } from "react-toastify";
import { isCurrentUserAdmin } from "../../utils/authRoles";

const Strategies = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStrategy, setEditStrategy] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [overrideDialog, setOverrideDialog] = useState({ open: false, strategy: null });

  const strategies = useSelector(selectStrategies);
  const managers = useSelector(selectManagers);
  const user = useSelector(getUserState);

  const isStrategyOwnedByCurrentUser = (strategy) => {
    const owner = strategy?.owner;
    if (!owner) return true;
    if (owner.startsWith("manager#")) return owner === `manager#${managers[0]?.managerId}`;
    if (owner.startsWith("user#")) return owner === `user#${user?.username}`;
    return true;
  };

  const handleAdd = () => {
    navigate("/strategies/add");
  };

  const handleEdit = (strategy) => {
    setEditStrategy(strategy);
    setDialogOpen(true);
  };

  const handlePermissions = (strategy) => {
    const id = strategy?.strategyId || strategy?.id;
    if (!id) {
      toast.error("Strategy id is missing");
      return;
    }
    navigate("/strategies/permissions", { state: { strategyId: id } });
  };

  const performDelete = async (strategy) => {
    const idToDelete = strategy?.strategyId || strategy?.id;
    if (!idToDelete) return;

    try {
      dispatch(addActiveRequests("delete_strategy"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Deleting Strategy..."));

      const resp = await deleteStrategies([idToDelete]);
      if (resp?.error) {
        const err = new Error(resp.message || "Delete failed");
        err.status = resp.status;
        err.code = resp.code;
        throw err;
      }

      dispatch(
        setStrategies((strategies || []).filter((s) => (s?.strategyId || s?.id) !== idToDelete))
      );

      dispatch(removeActiveRequests("delete_strategy"));
      dispatch(updateLoading(false));
      dispatch(updateLoadingMsg(""));
      toast.success("Strategy deleted");
    } catch (error) {
      console.error("Failed to delete strategy", error);
      dispatch(removeActiveRequests("delete_strategy"));
      dispatch(updateLoading(false));
      dispatch(updateLoadingMsg(""));

      const isAccessDenied =
        error?.status === 403 ||
        error?.code === "ACCESS_DENIED" ||
        error?.message === "You do not have permission to delete the requested strategies";

      const displayMessage = isAccessDenied
        ? "You do not have permissions to delete this strategy"
        : error?.message;
      toast.error(displayMessage);
    }
  };

  const handleDelete = (strategy) => {
    if (isCurrentUserAdmin() && !isStrategyOwnedByCurrentUser(strategy)) {
      setOverrideDialog({ open: true, strategy });
      return;
    }
    if (window.confirm(`Are you sure you want to delete strategy "${strategy?.name}"?`)) {
      performDelete(strategy);
    }
  };

  const handleOverrideCancel = () => setOverrideDialog({ open: false, strategy: null });

  const handleOverrideConfirm = () => {
    const strategy = overrideDialog.strategy;
    setOverrideDialog({ open: false, strategy: null });
    performDelete(strategy);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditStrategy(null);
  };

  const handleSave = async (strategy) => {
    if (!strategy) return;

    if (editStrategy) {
      const fields = {};
      if (strategy.description !== undefined) fields.description = strategy.description;
      if (strategy.owner !== undefined) fields.owner = strategy.owner;
      if (strategy.tags !== undefined) fields.tags = strategy.tags;
      if (strategy.categories !== undefined) fields.categories = strategy.categories;
      if (strategy.permissions !== undefined) fields.permissions = strategy.permissions;

      if (Object.keys(fields).length === 0) {
        toast.info("No changes to save");
        setDialogOpen(false);
        setEditStrategy(null);
        return;
      }

      try {
        dispatch(addActiveRequests("modify_strategy"));
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Updating Strategy..."));

        const resp = await updateStrategy(editStrategy.strategyId, fields);
        if (resp?.error) throw new Error(resp.message || "Update failed");

        // Response includes post-update permissions (and other fields) — refresh local state from it
        const nextPermissions =
          resp?.permissions ??
          resp?.strategy?.permissions ??
          resp?.data?.permissions;

        const updatedStrategies = (strategies || []).map((s) => {
          if (s.strategyId !== editStrategy.strategyId) return s;
          return {
            ...s,
            ...(resp?.name != null ? { name: resp.name } : {}),
            ...(resp?.date != null ? { date: resp.date } : {}),
            ...(fields.description !== undefined
              ? { description: fields.description }
              : {}),
            ...(fields.owner !== undefined ? { owner: fields.owner } : {}),
            // Full replace from response so removals (level 0) disappear locally
            ...(nextPermissions != null ? { permissions: nextPermissions } : {}),
            metadata: {
              ...s.metadata,
              ...(fields.tags !== undefined ? { tags: fields.tags } : {}),
              ...(fields.categories !== undefined
                ? { categories: fields.categories }
                : {}),
            },
          };
        });
        dispatch(setStrategies(updatedStrategies));

        dispatch(removeActiveRequests("modify_strategy"));
        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
        toast.success("Strategy updated");
      } catch (error) {
        console.error("Failed to update strategy", error);
        dispatch(removeActiveRequests("modify_strategy"));
        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
        toast.error(error?.message || "Failed to update strategy");
      }
    } else {
      if (!strategy || Object.keys(strategy).length === 0) return;
      sendWebSocketMessage(
        `{"action":"new_strategy", "data":${JSON.stringify(strategy)}}`
      );
      dispatch(addActiveRequests("new_strategy"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg(`Adding Strategy...`));
    }
    setDialogOpen(false);
    setEditStrategy(null);
  };

  return (
    <>
      <Helmet>
        <title>Strategies | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Strategies
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Manage your trading strategies and investment approaches
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <StrategiesTable
              strategies={strategies}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={handleAdd}
              onPermissions={handlePermissions}
            />
          </Card>
        </Container>

        <StrategiesDialog
          key={editStrategy?.strategyId || "new"}
          open={dialogOpen}
          onClose={handleDialogClose}
          strategy={editStrategy}
          onSave={handleSave}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />

        <Dialog open={overrideDialog.open} onClose={handleOverrideCancel}>
          <DialogTitle sx={{  fontSize: "15px" }}>Delete strategy owned by another user</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This strategy is not yours ("{overrideDialog.strategy?.name}"). Do you want to delete it anyway?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOverrideCancel}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleOverrideConfirm}>
              Delete anyway
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default Strategies;
